<template>
  <div class="overview-ppc-chart-panel p-5 mb-6" data-testid="overview-ppc-chart">
    <h2 class="overview-ppc-chart__title mb-4 m-0">
      {{ $t('UserProjects.overview.ppcChart.title') }}
    </h2>

    <div v-if="loading" class="text-sm text-[var(--color-disabled)] py-8 text-center">
      {{ $t('UserProjects.overview.loading') }}
    </div>

    <div v-else-if="error" class="text-sm text-[var(--color-error-text)] py-8 text-center">
      {{ error }}
    </div>

    <div v-else-if="isEmpty" class="text-sm text-[var(--color-disabled)] py-8 text-center h-48 flex items-center justify-center">
      {{ $t('UserProjects.overview.ppcChart.empty') }}
    </div>

    <div v-else class="overview-ppc-line-chart">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const referenceLinePlugin = {
  id: 'ppcReferenceLines',
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    if (!yScale || !chartArea) return;

    const lines = [
      { value: 75, color: '#22C55E' },
      { value: 50, color: '#EF4444' },
    ];

    lines.forEach(({ value, color }) => {
      const y = yScale.getPixelForValue(value);
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.restore();
    });
  },
};

ChartJS.register(referenceLinePlugin);

export default {
  name: 'OverviewPpcTrendChart',
  components: {
    Line,
  },
  computed: {
    ...mapGetters('pmPpc', {
      loading: 'loading',
      error: 'error',
      isEmpty: 'isEmpty',
      chartBars: 'chartBars',
    }),
    chartData() {
      return {
        labels: this.chartBars.map((bar) => bar.label),
        datasets: [
          {
            label: 'PPC',
            data: this.chartBars.map((bar) => bar.ppc),
            borderColor: '#e4821a',
            backgroundColor: 'rgba(228, 130, 26, 0.08)',
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#e4821a',
            pointBorderWidth: 2,
            pointHoverRadius: 6,
            tension: 0.3,
            fill: false,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            titleColor: '#1e2019',
            bodyColor: '#1e2019',
            borderColor: '#e5e5e5',
            borderWidth: 1,
            padding: 10,
            callbacks: {
              label: (context) => `${context.parsed.y}% PPC`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: '#ababab',
              font: { size: 11 },
            },
          },
          y: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 25,
              color: '#ababab',
              font: { size: 11 },
              callback: (value) => `${value}%`,
            },
            grid: {
              color: '#e5e5e5',
              drawTicks: false,
            },
            border: { display: false },
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.overview-ppc-chart-panel {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}

.overview-ppc-chart__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-dark);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.overview-ppc-line-chart {
  height: 240px;
}
</style>
